// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Renders the sources an answer is grounded in (T-301/T-303). Each citation links
// back to the relevant area of the app where a route exists; otherwise it shows a
// non-link badge. Grouped/deduped so repeated sources don't clutter the answer.

import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { FileText, BookOpen, Sprout, FlaskConical, ClipboardList, Leaf } from "lucide-react";
import type { Citation } from "@/hooks/prometheus-agent/usePrometheusAgent";

// Section-level routes that are known to exist (App.tsx). Deep-linking by id is a
// later refinement; section links already let the user verify the source.
const ROUTE_FOR_TABLE: Record<string, string> = {
  prometheus_reports: "/prometheus?tab=reports",
  sops: "/aurum/sops",
  aurum_sops: "/aurum/sops",
  strains: "/animus/library",
};

const ICON_FOR_KIND: Record<string, typeof FileText> = {
  sop: BookOpen,
  report: FileText,
  diary: Sprout,
  strain: Leaf,
  quality: FlaskConical,
  rows: ClipboardList,
  context: FileText,
};

function CitationChip({ citation }: { citation: Citation }) {
  const Icon = ICON_FOR_KIND[citation.kind] || FileText;
  const route = citation.source_table === "prometheus_reports" && citation.source_id ? `/prometheus?tab=reports&report=${encodeURIComponent(citation.source_id)}` : citation.source_table ? ROUTE_FOR_TABLE[citation.source_table] : undefined;

  const inner = (
    <Badge
      variant="outline"
      className="text-xs flex items-center gap-1 max-w-full hover:bg-muted/60 transition-colors"
      title={citation.label}
    >
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="truncate">{citation.label}</span>
    </Badge>
  );

  return route ? (
    <Link to={route} className="max-w-[260px]">{inner}</Link>
  ) : (
    <span className="max-w-[260px]">{inner}</span>
  );
}

export function Citations({ citations }: { citations: Citation[] }) {
  if (!citations || citations.length === 0) return null;

  // Dedupe by table+id (or label for non-record citations like SQL rows).
  const seen = new Set<string>();
  const unique = citations.filter((c) => {
    const key = `${c.source_table || c.kind}:${c.source_id || c.label}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <p className="text-xs font-medium mb-2 text-muted-foreground">Sources</p>
      <div className="flex flex-wrap gap-1.5">
        {unique.map((c, i) => (
          <CitationChip key={i} citation={c} />
        ))}
      </div>
    </div>
  );
}

export default Citations;
